import { computed, nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'

import { useWebSiteStore } from '@/stores/web-site.store'

export const MINUTES_PER_DAY = 1440
export const DAY_LINES_MIN_WIDTH = 1.25
export const HOUR_LINES_MIN_WIDTH = 20
export const TODAY_VIEWPORT_RATIO = 0.25
export const CENTER_VIEWPORT_RATIO = 0.5

const HOURS_PER_DAY = 24
const DAYS_PER_WEEK = 7
const EXTEND_DAYS = 60
const EDGE_DAYS = 14
const MAX_OFFSCREEN_DAYS = 2 * (EDGE_DAYS + EXTEND_DAYS)

// dayWidth in rem; snapMinutes is the drag granularity at that zoom.
export const TIMELINE_ZOOM_LEVELS = Object.freeze([
  { dayWidth: 0.375, snapMinutes: MINUTES_PER_DAY },
  { dayWidth: 0.75, snapMinutes: MINUTES_PER_DAY },
  { dayWidth: 1.25, snapMinutes: MINUTES_PER_DAY },
  { dayWidth: 2.5, snapMinutes: 360 },
  { dayWidth: 4.5, snapMinutes: 120 },
  { dayWidth: 7, snapMinutes: 60 },
  { dayWidth: 12, snapMinutes: 30 },
  { dayWidth: 24, snapMinutes: 15 },
  { dayWidth: 40, snapMinutes: 15 },
  { dayWidth: 72, snapMinutes: 5 },
])

const ZOOM_PRESETS = Object.freeze([
  { labelKey: 'common.month', zoomIndex: 1, fromIndex: 0 },
  { labelKey: 'common.week', zoomIndex: 3, fromIndex: 3 },
  { labelKey: 'common.day', zoomIndex: 5, fromIndex: 5 },
  { labelKey: 'common.hour', zoomIndex: 7, fromIndex: 7 },
])

// Scale, zoom, extendable date window, scrolling and background grid shared by the planner timeline
// and the warehouse availability: `scrollRef` is the native scroll element, `taskListWidth` (rem)
// the sticky left column, `onExtend` reloads the data after the window grew.
export function useTimelineViewport({ scrollRef, taskListWidth, zoomIndex, maxZoomIndex = TIMELINE_ZOOM_LEVELS.length - 1, taskListBounds, onExtend }) {
  const { t } = useI18n()
  const webSiteStore = useWebSiteStore()
  const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize)

  const rangeStart = ref(dayjs().startOf('day'))
  const rangeDays = ref(0)

  const zoomLevel = computed(() => TIMELINE_ZOOM_LEVELS[zoomIndex.value])
  const dayWidth = computed(() => zoomLevel.value.dayWidth)
  const trackWidth = computed(() => rangeDays.value * dayWidth.value)
  const canZoomIn = computed(() => zoomIndex.value < maxZoomIndex)
  const canZoomOut = computed(() => zoomIndex.value > 0)
  const dayPixels = () => dayWidth.value * rootFontSize
  const taskListPixels = () => taskListWidth.value * rootFontSize

  const presets = ZOOM_PRESETS.filter(p => p.zoomIndex <= maxZoomIndex)
  const activePreset = computed(() => [...presets].reverse().find(p => zoomIndex.value >= p.fromIndex).zoomIndex)
  const presetOptions = computed(() => presets.map(p => ({ value: p.zoomIndex, label: t(p.labelKey, 2) })))

  const dayIndexOf = (value) => {
    const date = dayjs(value)
    return date.startOf('day').diff(rangeStart.value, 'day') + (date.hour() * 60 + date.minute()) / MINUTES_PER_DAY
  }

  const dateAtClientX = (clientX, element) =>
    rangeStart.value.add(Math.floor((clientX - element.getBoundingClientRect().left) / dayPixels()), 'day')

  const viewportDays = () => Math.ceil((scrollRef.value.clientWidth - taskListPixels()) / dayPixels())

  const initRange = (focusDate, minSpanDays) => {
    const spanDays = Math.max(minSpanDays, viewportDays() + EDGE_DAYS)
    rangeStart.value = dayjs(focusDate).startOf('day').subtract(spanDays, 'day')
    rangeDays.value = spanDays * 2
  }

  const scrollToDate = (date, viewportRatio) => {
    const el = scrollRef.value
    if (!el) return
    el.scrollLeft = dayIndexOf(date) * dayPixels() - (el.clientWidth - taskListPixels()) * viewportRatio
  }

  const scrollToToday = () => scrollToDate(dayjs(), TODAY_VIEWPORT_RATIO)

  let extendingRange = false

  const prependDaysAsync = async(days) => {
    rangeStart.value = rangeStart.value.subtract(days, 'day')
    rangeDays.value += days
    await nextTick()
    scrollRef.value.scrollLeft += days * dayPixels()
  }

  const maxRangeDays = () => viewportDays() + MAX_OFFSCREEN_DAYS

  const trimRangeStartAsync = async() => {
    const excessDays = rangeDays.value - maxRangeDays()
    if (excessDays <= 0) return
    rangeStart.value = rangeStart.value.add(excessDays, 'day')
    rangeDays.value -= excessDays
    await nextTick()
    scrollRef.value.scrollLeft -= excessDays * dayPixels()
  }

  // A window narrower than the viewport never scrolls, so the edge checks below would never extend it.
  const padRangeToViewportAsync = async() => {
    const padDays = Math.max(Math.ceil((viewportDays() + 2 * EDGE_DAYS - rangeDays.value) / 2), 0)
    if (!padDays) return 0
    rangeDays.value += padDays
    await prependDaysAsync(padDays)
    onExtend()
    return padDays
  }

  const extendRangeIfNeededAsync = async() => {
    const el = scrollRef.value
    if (!el?.clientWidth || extendingRange) return
    const edge = EDGE_DAYS * dayPixels()
    extendingRange = true
    try {
      if (await padRangeToViewportAsync()) return
      if (el.scrollLeft < edge) {
        await prependDaysAsync(EXTEND_DAYS)
        rangeDays.value = Math.min(rangeDays.value, maxRangeDays())
        onExtend()
      } else if (el.scrollWidth - el.scrollLeft - el.clientWidth < edge) {
        rangeDays.value += EXTEND_DAYS
        await trimRangeStartAsync()
        onExtend()
      }
    } finally {
      extendingRange = false
    }
  }

  const setZoomAsync = async(index, anchorClientX = null) => {
    const next = Math.min(Math.max(index, 0), maxZoomIndex)
    const el = scrollRef.value
    if (next === zoomIndex.value || !el) return
    const trackViewportLeft = el.getBoundingClientRect().left + taskListPixels()
    const anchorOffset = anchorClientX === null
      ? (el.clientWidth - taskListPixels()) * CENTER_VIEWPORT_RATIO
      : anchorClientX - trackViewportLeft
    const anchorIndex = (el.scrollLeft + anchorOffset) / dayPixels()
    zoomIndex.value = next
    await nextTick()
    const padDays = await padRangeToViewportAsync()
    el.scrollLeft = (anchorIndex + padDays) * dayPixels() - anchorOffset
    extendRangeIfNeededAsync()
  }

  const onWheel = (event) => {
    if (!event.ctrlKey && !event.metaKey) return
    event.preventDefault()
    setZoomAsync(zoomIndex.value + (event.deltaY < 0 ? 1 : -1), event.clientX)
  }

  const resizeTaskList = ({ originX, width }, clientX) => {
    const next = width + (clientX - originX) / rootFontSize
    taskListWidth.value = Math.min(Math.max(next, taskListBounds.min), taskListBounds.max)
  }

  const restDaysGradient = (dw) => {
    const stops = [...webSiteStore.getRestDays()]
      .sort((a, b) => a - b)
      .map(isoWeekday => `transparent ${(isoWeekday - 1) * dw}rem, var(--timeline-weekend) ${(isoWeekday - 1) * dw}rem ${isoWeekday * dw}rem, transparent ${isoWeekday * dw}rem`)
    return stops.length ? `linear-gradient(to right, ${stops.join(', ')})` : null
  }

  const gridStyle = computed(() => {
    const dw = dayWidth.value
    const mondayOffset = ((DAYS_PER_WEEK - (rangeStart.value.day() + 6) % DAYS_PER_WEEK) % DAYS_PER_WEEK) * dw
    const weekWidth = DAYS_PER_WEEK * dw
    const restDays = restDaysGradient(dw)
    const layers = [
      ['linear-gradient(to right, var(--timeline-line) 0 1px, transparent 1px)',
        `${dw >= DAY_LINES_MIN_WIDTH ? dw : weekWidth}rem 100%`,
        `${dw >= DAY_LINES_MIN_WIDTH ? 0 : mondayOffset}rem 0`],
      ...(restDays ? [[restDays, `${weekWidth}rem 100%`, `${mondayOffset}rem 0`]] : []),
    ]
    if (dw >= HOUR_LINES_MIN_WIDTH) {
      layers.unshift(['linear-gradient(to right, var(--timeline-line-faint) 0 1px, transparent 1px)', `${dw / HOURS_PER_DAY}rem 100%`, '0 0'])
    }
    return {
      left: `${taskListWidth.value}rem`,
      width: `${trackWidth.value}rem`,
      backgroundImage: layers.map(l => l[0]).join(', '),
      backgroundSize: layers.map(l => l[1]).join(', '),
      backgroundPosition: layers.map(l => l[2]).join(', '),
    }
  })

  const todayLeft = computed(() => taskListWidth.value + dayIndexOf(dayjs()) * dayWidth.value)
  const todayBandStyle = computed(() => ({
    left: `${taskListWidth.value + dayIndexOf(dayjs().startOf('day')) * dayWidth.value}rem`,
    width: `${dayWidth.value}rem`,
  }))

  return {
    zoomIndex,
    zoomLevel,
    rangeStart,
    rangeDays,
    dayWidth,
    trackWidth,
    canZoomIn,
    canZoomOut,
    dayPixels,
    taskListPixels,
    activePreset,
    presetOptions,
    dayIndexOf,
    dateAtClientX,
    initRange,
    scrollToDate,
    scrollToToday,
    extendRangeIfNeededAsync,
    setZoomAsync,
    onWheel,
    resizeTaskList,
    gridStyle,
    todayLeft,
    todayBandStyle,
  }
}
